import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';
import { getAdminSession } from '../../adminSession';
import '../../Admin.scss';

type AuditLogEntry = {
  id: number;
  actorId: number | null;
  actorName: string | null;
  actorEmail: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  detail: string | null;
  createdAt: string;
};

type ApiEnvelope<T> = { ok: boolean; data?: T; message?: string };

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${yyyy}.${mm}.${dd} ${hh}:${min}`;
}

function errorMessageOf(err: unknown, fallback: string): string {
  const data = axios.isAxiosError(err) ? err.response?.data : null;
  if (data && typeof data === 'object' && 'message' in data) {
    return String((data as { message?: string }).message) || fallback;
  }
  return fallback;
}

export default function AuditLogPage() {
  const session = getAdminSession();
  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const fetchItems = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get<ApiEnvelope<AuditLogEntry[]>>(`${MainURL}/admin-console/audit-logs`, {
        params: { requesterId: session.id, limit: 200 },
      });
      if (res.data?.ok && res.data.data) {
        setItems(res.data.data);
      } else {
        setErrorMsg(res.data?.message || '작업 이력을 불러오지 못했습니다.');
      }
    } catch (err) {
      setErrorMsg(errorMessageOf(err, '작업 이력을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return (
    <div className="Main-cover admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2 className="admin-panel-title">작업 이력 (Audit Log)</h2>
          <p className="admin-panel-desc">정보 수정 및 작업 이력 로그를 추적합니다. 최근 200건까지 표시됩니다.</p>
        </div>
        <button type="button" className="admin-refresh-btn" onClick={fetchItems} disabled={loading}>
          {loading ? '불러오는 중...' : '새로고침'}
        </button>
      </div>

      {errorMsg && <p className="admin-error-text">{errorMsg}</p>}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>시각</th>
              <th>작업자</th>
              <th>액션</th>
              <th>대상</th>
              <th>상세</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={5}>기록된 작업 이력이 없습니다.</td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td>{formatDateTime(item.createdAt)}</td>
                  <td>
                    {item.actorName || '-'}
                    {item.actorEmail ? ` (${item.actorEmail})` : ''}
                  </td>
                  <td>{item.action}</td>
                  <td>
                    {item.targetType || '-'}
                    {item.targetId ? ` #${item.targetId}` : ''}
                  </td>
                  <td style={{ maxWidth: 320, whiteSpace: 'pre-wrap', textAlign: 'left', fontSize: 12 }}>
                    {item.detail || '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
